// Lyrics panel — legally-sourced lyrics only. Fetches from the licensed backend adapter;
// renders synchronized / plain / instrumental / unavailable / restricted honestly. Never
// scrapes, never generates, never invents timestamps. Respects reduced-motion.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ExternalLink, Quote } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { openProviderUrl } from '@/lib/providerLinks';
import { getSongLyrics, type LyricsResultDTO } from '@/services/performanceService';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setAutoScroll } from './playerSlice';
import { usePlayer } from './usePlayer';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const LyricsPanel: React.FC = () => {
  const { snap, seek } = usePlayer();
  const dispatch = useAppDispatch();
  const autoScroll = useAppSelector((s) => (s as any).player.autoScrollLyrics as boolean);
  const [result, setResult] = useState<LyricsResultDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const item = snap.item;

  useEffect(() => {
    if (!item) { setResult(null); return; }
    let alive = true;
    setLoading(true); setResult(null);
    getSongLyrics({ isrc: item.isrc, title: item.title, artist: item.artist || item.subtitle || '', album: item.album, durationMs: item.durationMs, appleMusicCatalogId: item.id })
      .then((r) => { if (alive) setResult(r); })
      .catch(() => { if (alive) setResult({ status: 'unavailable' }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [item?.id]);

  const activeIndex = useMemo(() => {
    if (result?.status !== 'available' || result.mode !== 'synchronized') return -1;
    let idx = -1;
    result.lines.forEach((l, i) => { if ((l.startMs ?? Infinity) <= snap.positionMs) idx = i; });
    return idx;
  }, [result, snap.positionMs]);

  useEffect(() => {
    if (autoScroll && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }
  }, [activeIndex, autoScroll]);

  if (!item) return <Empty text="Play a song to see lyrics." />;
  if (loading) return <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  if (result?.status === 'available') {
    const sync = result.mode === 'synchronized';
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="text-xs font-medium text-muted-foreground">{sync ? 'Synchronized lyrics' : 'Lyrics'}</span>
          {sync && (
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" className="h-3.5 w-3.5 accent-orange-500" checked={autoScroll} onChange={(e) => dispatch(setAutoScroll(e.target.checked))} />
              Auto-scroll
            </label>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pr-1" aria-live="off">
          <ol className="space-y-3 py-2">
            {result.lines.map((line, i) => {
              const active = i === activeIndex;
              return (
                <li key={i}>
                  <button
                    ref={active ? activeRef : undefined}
                    type="button"
                    disabled={!sync || line.startMs == null}
                    onClick={() => sync && line.startMs != null && seek(line.startMs / 1000)}
                    aria-current={active ? 'true' : undefined}
                    className={`w-full text-left text-lg font-semibold leading-snug transition-colors ${
                      active ? 'text-foreground' : 'text-muted-foreground/60'
                    } ${sync && line.startMs != null ? 'hover:text-foreground focus:text-foreground focus:outline-none focus-visible:underline' : 'cursor-default'}`}
                    dir="auto"
                  >
                    {line.text || ' '}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
        <Attribution text={result.attribution.text} url={result.attribution.url} />
      </div>
    );
  }

  const reason = result?.status === 'instrumental' ? 'This track is instrumental.'
    : result?.status === 'restricted' ? 'Lyrics aren’t licensed for your region yet.'
    : 'Lyrics unavailable in KaziPeople.';
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <Quote className="h-8 w-8 text-muted-foreground/50" />
      <p className="text-sm font-medium text-foreground">{reason}</p>
      {item.canonicalUrl && (
        <Button size="sm" variant="outline" onClick={() => openProviderUrl(item.canonicalUrl!)}>
          <ExternalLink className="mr-1.5 h-4 w-4" />Open in Apple Music
        </Button>
      )}
      {result?.attribution && <Attribution text={result.attribution.text} url={result.attribution.url} />}
    </div>
  );
};

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">{text}</div>
);
const Attribution: React.FC<{ text: string; url?: string }> = ({ text, url }) => (
  <p className="pt-2 text-[11px] text-muted-foreground">
    {url ? <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">{text}</a> : text}
  </p>
);
